
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Prop, Watch } from "vue-property-decorator";
    import ApiClient from "@/utilities/ApiClient";
    import Utils from "@/utilities/Utils";
    import { mapData } from "@/utilities/DataMapping";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import SiteRecipientDialogue from "@/vue/components/SiteRecipientDialogue.vue";
    import ConfirmDialogue from "@/vue/components/ConfirmDialogue.vue";
    import EmailList from "@/vue/components/EmailList.vue";
    import { SiteRecipientSearchParameters } from "@/model/SiteRecipientSearchParameters";
    import { ISiteRecipient, SiteRecipient } from "@/model/SiteRecipient";
    import { ISaveResponse } from "@/model/ISaveResponse";
    import { IBriefingPathway } from "@/model/BriefingPathway";

    enum ToggleFlag { None, Active, Sponsor, CarbonCopy }

    @Component({
        components: { ApiButton, SiteRecipientDialogue, ConfirmDialogue, EmailList }
    })
    export default class SiteRecipients extends Vue {

        async mounted(): Promise<void> {
            await this.loadSiteRecipients();
        }

        @Prop({ required: true })
        siteID!: string;

        @Prop({ required: true })
        pathwayList!: Array<IBriefingPathway>;

        get isNewSite(): boolean {
            return Utils.isEmptyId(this.siteID);
        }

        infiniteId: number = + new Date();
        searchParameters = new SiteRecipientSearchParameters();
        siteRecipientList: Array<SiteRecipient> = [];

        //
        // -- Watchers
        //

        debouncedRefreshSearch = Utils.debounce(this.loadSiteRecipients, 200);

        @Watch("searchParameters.searchText")
        private onSearchTextchanged(): void {
            this.debouncedRefreshSearch();
        }

        @Watch("siteID")
        private onSiteIDchanged(): void {
            this.loadSiteRecipients();
        }

        onSaved(): void {
            this.$emit("changed");
            this.loadSiteRecipients();
        }

        //
        // -- methods
        //

        pathwaysHtml(siteRecipient: SiteRecipient): string {
            if(siteRecipient.isCarbonCopyAllPathways) return `<span class="all-label">  <i class="mdi mdi-all-inclusive mr-2"></i>  all pathways</span>`;
            if(siteRecipient.pathwayIDs.length === 0) return `<span class="none-label">none</span>`;
            let html = "<ul>";
            for (const pathwayID of siteRecipient.pathwayIDs) {
                const item = this.pathwayList.filter(l => l.pathwayID === pathwayID)[0];
                html += item
                    ? `<li>${item.briefingName}: <span class="pathway-description">${item.pathwayDescription}</span></li>`
                    : `<li>${pathwayID}</li>`;
            }
            html += "</ul>";
            return html;
        }

        editNew(): void {
            const dialog: SiteRecipientDialogue = this.$refs.siteRecipientDialogue as SiteRecipientDialogue;
            dialog.add(this.siteID);
        }

        edit(siteRecipient: ISiteRecipient): void {
            const dialog: SiteRecipientDialogue = this.$refs.siteRecipientDialogue as SiteRecipientDialogue;
            dialog.edit(siteRecipient.id);
        }

        async loadSiteRecipients(): Promise<void> {
            if(this.isNewSite) {
                 this.siteRecipientList = [];
                 return;
            }
            this.searchParameters.siteID = this.siteID;
            const data = await ApiClient.post("/Api/SiteRecipient/Search", this.searchParameters);
            this.siteRecipientList = mapData<Array<SiteRecipient>>(data, { root: (s) => new SiteRecipient (s) });
        }

        //
        // -- toggling flags in search results (requested by client)
        //

        toggleFlagEnum: any = ToggleFlag; // for use in markup
        recipientToToggle: SiteRecipient|null = null;
        whichFlagToToggle: ToggleFlag = ToggleFlag.None;

        getFlagProperty(flag: ToggleFlag): string {
            switch(flag){
                case ToggleFlag.Active: return "isActive";
                case ToggleFlag.Sponsor: return "isSponsor";
                case ToggleFlag.CarbonCopy: return "isCarbonCopyAllPathways";
                default: return "nope";
            }
        }

        getFlagValue(recipient: SiteRecipient, whichFlag: ToggleFlag): boolean {
            return (recipient as any)[this.getFlagProperty(whichFlag)] as boolean;
        }

        setFlagValue(recipient: SiteRecipient, whichFlag: ToggleFlag, value: boolean): void {
            (recipient as any)[this.getFlagProperty(whichFlag)] = value;
        }

        confirmToggleFlag(recipient: SiteRecipient, whichFlag: ToggleFlag): void {
            this.recipientToToggle = recipient;
            this.whichFlagToToggle = whichFlag;
            const flagValue = this.getFlagValue(recipient, whichFlag);
            const dialog: ConfirmDialogue = this.$refs.confirmToggleFlagDialogue as ConfirmDialogue;
            const flagDescription = Utils.spacify(ToggleFlag[whichFlag]);
            const verb = flagValue ? "un-set" : "set";
            dialog.title = `${flagValue ? "Un-Set" : "Set"} ${Utils.spacify(ToggleFlag[whichFlag])}`;
            dialog.text = `Do you want to <b>${verb}</b> the <b>${flagDescription}</b> flag for <b>${recipient.name}</b> now?`;
            dialog.yesButtonText = `Yes, ${verb}`;
            dialog.show();
        }

        async toggleFlag(): Promise<void> {
            if(this.recipientToToggle == null) return;
            const flagValue = this.getFlagValue(this.recipientToToggle, this.whichFlagToToggle);
            this.setFlagValue(this.recipientToToggle, this.whichFlagToToggle, !flagValue);
            const response: ISaveResponse = await ApiClient.post("/api/siteRecipient/save", this.recipientToToggle, "set-recipient-flag");
            const dialog: ConfirmDialogue = this.$refs.confirmToggleFlagDialogue as ConfirmDialogue;
            this.recipientToToggle = null;
            this.whichFlagToToggle = ToggleFlag.None;
            dialog.hide();
            this.loadSiteRecipients(); // ??
        }


    }
